
import type {
  QueryRankingDTO,
  QueryRankingElementRankingDTO,
  QueryRankingHiddenElementDTO,
  QueryRankingPinnedElementDTO,
} from '@empathyco/playboard-core-domain'
import type { PropType } from 'vue'
import type { PivotableAudit } from '../../../../composables'
import type { AuditItem, AuditMessages, ConfigurationsMessages } from '../../../../models'
import type { AuditItemDetailsMessages } from './audit-item-details.component.vue'
import { defineComponent, getCurrentInstance } from 'vue'
import { useInstance, usePivotableAudit } from '../../../../composables'

import RankingElementsListItem from '../../../configurations/ranking-elements-list-item.component.vue'
import AuditItemDetails from './audit-item-details.component.vue'

type AuditQueryRanking = Pick<QueryRankingDTO, 'id' | 'metadata' | 'title'> & {
  criteria: PivotableAudit & { queries: string; enabled: string }
} & {
  productsRanking: Record<string, QueryRankingElementRankingDTO>
  attributesRanking: Record<string, QueryRankingElementRankingDTO>
  hiddenAttributes: Record<string, QueryRankingHiddenElementDTO>
  pinnedProducts: Record<string, QueryRankingPinnedElementDTO>
  hiddenProducts: Record<string, QueryRankingHiddenElementDTO>
  restrictedRoles: string
}

interface SlotCommonElementProperties {
  slotId: string
  actionLabel: string
}

type QueryRankingElementConfig =
  | QueryRankingElementRankingDTO
  | QueryRankingHiddenElementDTO
  | QueryRankingPinnedElementDTO
type SlotElementRanking = QueryRankingElementConfig & SlotCommonElementProperties

export default defineComponent({
  name: 'QueryRankingAuditItemDetails',
  components: {
    AuditItemDetails,
    RankingElementsListItem,
  },
  props: {
    item: {
      type: Object as PropType<AuditItem<QueryRankingDTO>>,
      required: true,
    },
    messages: {
      type: Object as PropType<AuditMessages['entry']>,
      required: true,
    },
  },
  setup(props) {
    const {
      proxy: { $epb },
    } = getCurrentInstance()
    const { transform: transformPivots } = usePivotableAudit()
    const { rankableFieldConfigs } = useInstance()

    const toolMessages: ConfigurationsMessages = $epb.messages.tools.configurations
    const auditItemDetailsItem: AuditItem<AuditQueryRanking> = {
      ...props.item,
      payload: {
        ...props.item.payload,
        previousState: props.item.payload.previousState
          ? transformState(props.item.payload.previousState)
          : undefined,
        nextState: props.item.payload.nextState
          ? transformState(props.item.payload.nextState)
          : undefined,
      },
    }

    const slotElement: {
      nextState: Array<SlotElementRanking>
      previousState: Array<SlotElementRanking>
    } = {
      nextState: [
        ...(props.item.payload.nextState?.productsRanking.map(
          buildSlotElementConfig('productsRanking'),
        ) ?? []),
        ...(props.item.payload.nextState?.attributesRanking.map(
          buildSlotElementConfig('attributesRanking'),
        ) ?? []),
        ...(props.item.payload.nextState?.hiddenAttributes.map(
          buildSlotElementConfig('hiddenAttributes'),
        ) ?? []),
        ...(props.item.payload.nextState?.pinnedProducts.map(
          buildSlotElementConfig('pinnedProducts'),
        ) ?? []),
        ...(props.item.payload.nextState?.hiddenProducts.map(
          buildSlotElementConfig('hiddenProducts'),
        ) ?? []),
      ],
      previousState: [
        ...(props.item.payload.previousState?.productsRanking.map(
          buildSlotElementConfig('productsRanking'),
        ) ?? []),
        ...(props.item.payload.previousState?.attributesRanking.map(
          buildSlotElementConfig('attributesRanking'),
        ) ?? []),
        ...(props.item.payload.previousState?.hiddenAttributes.map(
          buildSlotElementConfig('hiddenAttributes'),
        ) ?? []),
        ...(props.item.payload.previousState?.pinnedProducts.map(
          buildSlotElementConfig('pinnedProducts'),
        ) ?? []),
        ...(props.item.payload.previousState?.hiddenProducts.map(
          buildSlotElementConfig('hiddenProducts'),
        ) ?? []),
      ],
    }

    function buildSlotElementConfig<T extends QueryRankingElementConfig>(
      type:
        | 'productsRanking'
        | 'attributesRanking'
        | 'hiddenAttributes'
        | 'pinnedProducts'
        | 'hiddenProducts',
    ): (element: T) => SlotElementRanking {
      return (element: T): SlotElementRanking => {
        const name =
          type === 'attributesRanking' || type === 'hiddenAttributes'
            ? buildAttributeLabel(element)
            : element.fieldValue

        return {
          ...element,
          slotId: `${type}.${name}`,
          actionLabel: toolMessages.actionLabels[element.action].positive,
        }
      }
    }

    const transformedMessages: AuditItemDetailsMessages = {
      previousState: props.messages.previousState,
      nextState: props.messages.nextState,
      fields: {
        updated: toolMessages.fields.updateDate.label,
        created: toolMessages.fields.creationDate.label,
        author: toolMessages.fields.user.label,
        enabled: toolMessages.fields.enabled.label,
        title: toolMessages.fields.name.label,
        restrictedRoles: toolMessages.fields.restricted.label,
        productsRanking: toolMessages.fields.products.label,
        attributesRanking: toolMessages.fields.attributes.label,
        hiddenAttributes: toolMessages.fields.hiddenAttributes.label,
        pinnedProducts: toolMessages.fields.pinnedProducts.label,
        hiddenProducts: toolMessages.fields.hiddenProducts.label,
        ...slotElement.nextState.reduce(addRankingElementMessages, {}),
        ...slotElement.previousState.reduce(addRankingElementMessages, {}),
      },
    }

    function transformState(state: QueryRankingDTO): AuditQueryRanking {
      return {
        id: state.id,
        restrictedRoles: state.restrictedRoles.join(', '),
        title: state.title,
        metadata: state.metadata,
        productsRanking: transformConfigElementState(state.productsRanking),
        attributesRanking: transformConfigElementState(state.attributesRanking, true),
        hiddenAttributes: transformConfigElementState(state.hiddenAttributes, true),
        pinnedProducts: transformConfigElementState(state.pinnedProducts),
        hiddenProducts: transformConfigElementState(state.hiddenProducts),
        criteria: {
          enabled: state.criteria.enabled
            ? toolMessages.fields.enabled.states.enabled
            : toolMessages.fields.enabled.states.disabled,
          queries: state.criteria.queries.join(', '),
          ...transformPivots(state.criteria),
        },
      }
    }

    function transformConfigElementState<T extends QueryRankingElementConfig>(
      elements: Array<T>,
      isAttribute = false,
    ): Record<string, T> {
      return elements.reduce((result, element) => {
        const filters = Object.entries(element.filters)
        const key = isAttribute ? buildAttributeLabel(element) : element.fieldValue
        return {
          ...result,
          [key]: {
            ...((element.schedule.activationDate || element.schedule.deactivationDate) &&
              element.schedule),
            action: element.action,
            ...('position' in element && { position: element.position }),
            ...(filters.length > 0 && {
              filters: filters.reduce(
                (filters, [filter, values]) => ({ ...filters, [filter]: values.join(', ') }),
                {},
              ),
            }),
          },
        }
      }, {})
    }

    function getAttributeName(element: QueryRankingElementConfig): string {
      const rankableField = rankableFieldConfigs.value.find(
        rankableField => rankableField.id === element.fieldName,
      )

      return rankableField?.name ?? element.fieldName
    }

    function buildAttributeLabel(element: QueryRankingElementConfig): string {
      return `${getAttributeName(element)} (${element.fieldValue})`
    }

    function addRankingElementMessages(
      elements: Record<string, unknown>,
      element: SlotElementRanking,
    ): Record<string, unknown> {
      elements[`${element.slotId}.action`] = toolMessages.fields.action.label
      elements[`${element.slotId}.activationDate`] = toolMessages.fields.activationDate.label
      elements[`${element.slotId}.deactivationDate`] = toolMessages.fields.deactivationDate.label
      elements[`${element.slotId}.filters`] = toolMessages.sections.filters.title
      elements[`${element.slotId}.position`] =
        toolMessages.modals.pinnedProducts.fields.position.label
      return elements
    }

    return {
      baseClass: 'query-ranking-audit-item-details',
      auditItemDetailsItem,
      transformedMessages,
      slotElement,
    }
  },
})
